import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const IMAGE_VIEW_KEY = 'product-image-view';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('paginated-list:new-elements', this.#applyImageViewToNewElements);
    this.setAttribute('initialized', '');
    const ghostSwitcherEnabled = this.getAttribute('data-ghost-switcher-enabled') !== 'false';

    // URL override: e.g. /collections/x?featured_image=ghost opens straight in
    // Ghost view (campaign/deep links) with no manual selection. This wins over
    // both the stored selection and the switcher setting — the whole point is to
    // force ghost even when the on-page selector is hidden. Persisted so load-more
    // and re-renders keep the forced view.
    const urlImageView = this.#imageViewFromUrl();
    this.imageViewForcedByUrl = urlImageView != null;

    let storedImageView;
    if (urlImageView) {
      storedImageView = urlImageView;
      sessionStorage.setItem(IMAGE_VIEW_KEY, urlImageView);
    } else if (ghostSwitcherEnabled) {
      storedImageView = sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model';
    } else {
      storedImageView = 'model';
      sessionStorage.setItem(IMAGE_VIEW_KEY, 'model');
    }

    requestIdleCallback(() => {
      this.#setImageView(storedImageView);
      const radio = this.querySelector(
        `input[type="radio"][name="product-image-view"][value="${storedImageView}"]`
      );
      if (radio instanceof HTMLInputElement) radio.checked = true;
      const toggle = this.querySelector('input[data-image-view-toggle]');
      if (toggle instanceof HTMLInputElement) toggle.checked = storedImageView === 'ghost';
      this.#applySizeStockFilter();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('paginated-list:new-elements', this.#applyImageViewToNewElements);
  }

  /** Re-apply current image view (model/ghost) when load more adds new cards. Only processes new elements to avoid O(n) over whole grid. */
  #applyImageViewToNewElements = (event) => {
    const ghostSwitcherEnabled = this.getAttribute('data-ghost-switcher-enabled') !== 'false';
    const value =
      ghostSwitcherEnabled || this.imageViewForcedByUrl
        ? (sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model')
        : 'model';
    const newElements = event?.detail?.elements;
    const scope = Array.isArray(newElements) && newElements.length > 0 ? newElements : null;
    this.#setImageView(value, scope);
    requestAnimationFrame(() => {
      this.#setImageView(value, scope);
      setTimeout(() => this.#setImageView(value, scope), 50);
    });
    // New cards were appended — re-run the in-stock size filter across the grid so
    // sold-out-size products from the new page are hidden and the count stays right.
    this.#applySizeStockFilter();
  };

  updatedCallback() {
    super.updatedCallback();
    const value = sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model';
    requestIdleCallback(() => {
      this.#setImageView(value);
      this.#applySizeStockFilter();
    });
  }

  /**
   * Reads the image view from the URL query (`featured_image=ghost|model`).
   * Lets deep links open a collection in Ghost view without touching the switcher.
   * @returns {'ghost' | 'model' | null} the requested view, or null when absent/invalid
   */
  #imageViewFromUrl() {
    try {
      const raw = (new URLSearchParams(window.location.search).get('featured_image') || '')
        .trim()
        .toLowerCase();
      if (raw === 'ghost') return 'ghost';
      if (raw === 'model') return 'model';
    } catch (_error) {
      /* malformed URL — ignore and fall back to stored/default view */
    }
    return null;
  }

  /**
   * Updates the image view (Model / Ghost). Called from image-view-controls (radio variant).
   * @param {Event} event - change event from radio input
   */
  updateImageView({ target }) {
    if (!(target instanceof HTMLInputElement) || !target.value) return;
    const value = target.value === 'ghost' ? 'ghost' : 'model';
    sessionStorage.setItem(IMAGE_VIEW_KEY, value);
    this.#setImageView(value);
  }

  /**
   * Updates the image view from the Ghost toggle (checkbox). Checked = ghost, unchecked = model.
   * @param {Event} event - change event from checkbox
   */
  updateImageViewFromToggle({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const value = target.checked ? 'ghost' : 'model';
    sessionStorage.setItem(IMAGE_VIEW_KEY, value);
    this.#setImageView(value);
  }

  /**
   * Sets product-image-view on grid and swaps card images (model vs ghost).
   * Uses the first slide (card cover) so video covers are handled reliably.
   * @param {string} value - 'model' | 'ghost'
   * @param {Element[] | null} [scopeElements] - If set, only process galleries inside these elements (e.g. newly loaded cards); otherwise processes all.
   */
  #setImageView(value, scopeElements = null) {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (grid) grid.setAttribute('product-image-view', value);
    const galleries =
      scopeElements?.length > 0
        ? scopeElements.flatMap((el) => Array.from(el.querySelectorAll('.card-gallery[data-ghost-src]')))
        : Array.from(this.querySelectorAll('.card-gallery[data-ghost-src]'));
    const useGhost = value === 'ghost';
    galleries.forEach((gallery) => {
      const firstSlide = gallery.querySelector('slideshow-slide');
      const container = firstSlide ?? gallery;
      const video = container.querySelector('video');
      const modelSrc = gallery.dataset.modelSrc;
      const ghostSrc = gallery.dataset.ghostSrc;

      if (video && ghostSrc) {
        this.#setImageViewForVideoCover(video, ghostSrc, useGhost);
      } else {
        const img = container.querySelector('img');
        if (img && !img.closest('video')) {
          this.#setImageViewForImage(img, gallery, modelSrc, ghostSrc, useGhost);
        }
      }
    });
  }

  /**
   * Swaps a single card's image (model vs ghost). Clears srcset when showing ghost.
   */
  #setImageViewForImage(img, gallery, modelSrc, ghostSrc, useGhost) {
    if (useGhost && ghostSrc) {
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.currentSrc || img.getAttribute('src') || modelSrc || '';
        const srcset = img.getAttribute('srcset');
        if (srcset) img.dataset.originalSrcset = srcset;
      }
      img.setAttribute('src', ghostSrc);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    } else {
      const restoreSrc = img.dataset.originalSrc || modelSrc || img.getAttribute('src');
      img.setAttribute('src', restoreSrc);
      if (img.dataset.originalSrcset) {
        img.setAttribute('srcset', img.dataset.originalSrcset);
        delete img.dataset.originalSrcset;
      }
      delete img.dataset.originalSrc;
    }
  }

  /**
   * When the card cover is a video: in ghost mode show ghost image and hide video; in model mode show video and hide ghost img.
   */
  #setImageViewForVideoCover(video, ghostSrc, useGhost) {
    const mediaContainer = video.closest('.product-media') ?? video.parentElement;
    if (!mediaContainer) return;

    let ghostImg = mediaContainer.querySelector('.card-gallery__ghost-overlay');
    if (useGhost && ghostSrc) {
      if (!ghostImg) {
        ghostImg = document.createElement('img');
        ghostImg.src = ghostSrc;
        ghostImg.alt = '';
        ghostImg.loading = 'lazy';
        ghostImg.decoding = 'async';
        ghostImg.className = 'product-media__image card-gallery__ghost-overlay';
        ghostImg.setAttribute('data-ghost-overlay', '');
        mediaContainer.style.position = 'relative';
        mediaContainer.prepend(ghostImg);
      } else {
        ghostImg.src = ghostSrc;
        ghostImg.hidden = false;
        ghostImg.style.display = '';
      }
      video.hidden = true;
      video.style.setProperty('display', 'none', 'important');
    } else {
      if (ghostImg) {
        ghostImg.hidden = true;
        ghostImg.style.display = 'none';
      }
      video.hidden = false;
      video.style.removeProperty('display');
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    try {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } catch (_e) {
      // If View Transitions fail (unsupported browser / script interference), still apply the layout.
      this.#setLayout(value);
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Reads the active size filters from the URL. Shopify's size facet is variant
   * option based (e.g. `filter.v.option.Size=M`), so it matches any product that
   * *has* an M variant — even when M is sold out. Shopify has no variant-level
   * availability filter, so we use these values to hide products whose selected
   * size isn't in stock (checked against each card's size strip in the DOM).
   * @returns {{ sizes: Set<string>, hasGroupedValue: boolean }} resolvable (trimmed, lowercased)
   *   size values, plus a flag when a grouped/GID value is present that can't be matched here
   */
  #getActiveSizeFilters() {
    const sizes = new Set();
    let hasGroupedValue = false;
    try {
      for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
        const normalizedKey = key.toLowerCase();
        if (!normalizedKey.startsWith('filter.') || !normalizedKey.includes('size')) continue;
        const normalizedValue = value.trim().toLowerCase();
        if (!normalizedValue) continue;
        // Search & Discovery "grouped" filter values (e.g. an "Other" bucket) arrive as a
        // FilterSettingGroup GID, not a real size label, so they can't be matched against a
        // card's size strip. Flag them so we skip client-side hiding for that request and let
        // the server (availability filter) handle grouped values — otherwise we'd hide every card.
        if (normalizedValue.startsWith('gid:')) {
          hasGroupedValue = true;
          continue;
        }
        sizes.add(normalizedValue);
      }
    } catch (_error) {
      /* malformed URL — treat as no active size filter */
    }
    return { sizes, hasGroupedValue };
  }

  /**
   * When a size filter is active, hides products whose selected size(s) are all
   * sold out. Each card's size strip (`.size-quick-add`) already marks sold-out
   * sizes, so stock is read straight from the DOM. Products without a size strip
   * (no size option / Early Access) stay visible — their stock can't be confirmed.
   * Runs on load, after Load more, and after filter changes.
   */
  #applySizeStockFilter() {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    const { sizes: activeSizes, hasGroupedValue } = this.#getActiveSizeFilters();

    // Nothing we can evaluate on the client: either no size filter, or a grouped/GID
    // value (e.g. "Other") that maps to no literal size label. Leave every card visible
    // and let the server-side availability filter handle it — never hide blindly here.
    if (hasGroupedValue || activeSizes.size === 0) {
      items.forEach((item) => this.#setSizeFilteredOut(item, false));
      this.#updateVisibleProductCount(false);
      return;
    }

    items.forEach((item) => {
      const strip = item.querySelector('.size-quick-add');
      if (!strip) {
        // No size data on this card — keep it visible (stock can't be confirmed).
        this.#setSizeFilteredOut(item, false);
        return;
      }

      let selectedSizeInStock = false;
      for (const button of strip.querySelectorAll('.size-quick-add__button')) {
        const label = (button.querySelector('.size-quick-add__label')?.textContent || '')
          .trim()
          .toLowerCase();
        if (!label || !activeSizes.has(label)) continue;
        const soldOut =
          button.classList.contains('size-quick-add__button--unavailable') ||
          button.hasAttribute('disabled');
        if (!soldOut) {
          selectedSizeInStock = true;
          break;
        }
      }

      this.#setSizeFilteredOut(item, !selectedSizeInStock);
    });

    this.#updateVisibleProductCount(true);
  }

  /**
   * Toggles the "hidden because the selected size is sold out" state on a grid item.
   * @param {Element} item - the `.product-grid__item` wrapper
   * @param {boolean} hidden
   */
  #setSizeFilteredOut(item, hidden) {
    if (!(item instanceof HTMLElement)) return;
    if (hidden) {
      item.classList.add('is-size-filtered-out');
      item.setAttribute('hidden', '');
    } else if (item.classList.contains('is-size-filtered-out')) {
      item.classList.remove('is-size-filtered-out');
      item.removeAttribute('hidden');
    }
  }

  /**
   * Adjusts the plain product-count label (`[data-product-count]`) to match what's
   * actually visible after sold-out-size products are hidden. Only updated when the
   * whole result set is already loaded, so the number never contradicts unseen pages;
   * multi-page range summaries (`[data-pagination-range-top]`) are server-driven and left alone.
   * @param {boolean} sizeFilterActive
   */
  #updateVisibleProductCount(sizeFilterActive) {
    const counts = document.querySelectorAll('[data-product-count]');
    if (counts.length === 0) return;

    const restore = () =>
      counts.forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.countOriginal != null) {
          el.textContent = el.dataset.countOriginal;
        }
      });

    if (!sizeFilterActive) {
      restore();
      return;
    }

    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (!grid) return;

    const totalItems = parseInt(grid.getAttribute('total-items') || '0', 10);
    const loaded = grid.querySelectorAll(':scope > .product-grid__item').length;
    // Only adjust once everything is loaded; otherwise the "N items" total spans
    // not-yet-loaded pages we can't inspect, so leave the server value in place.
    if (totalItems > 0 && loaded < totalItems) {
      restore();
      return;
    }

    const visible = grid.querySelectorAll(
      ':scope > .product-grid__item:not(.is-size-filtered-out)'
    ).length;

    counts.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.dataset.countOriginal == null) el.dataset.countOriginal = el.textContent || '';
      el.textContent = (el.dataset.countOriginal || '').replace(/\d[\d,]*/, String(visible));
    });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    const inputName = viewport === 'desktop' ? 'grid' : 'grid-mobile';
    const targetElement = this.querySelector(
      `input[type="radio"][name="${inputName}"][value="${storedLayout}"]`
    );

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayout(storedLayout);
      return;
    }

    // Fallback: if no matching option exists for this viewport, reset to default.
    const fallback = this.querySelector(
      viewport === 'desktop' ? '[data-grid-layout="desktop-default-option"]' : '[data-grid-layout="mobile-option"]'
    );
    if (fallback instanceof HTMLInputElement) {
      fallback.checked = true;
    }
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
